import { Link, Outlet } from "react-router-dom"
import { Fragment, useEffect, useState } from 'react'
import {
    Dialog,
    DialogBackdrop,
    DialogPanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Transition,
    TransitionChild,
} from '@headlessui/react'
import {
    Bars3Icon,
    BellIcon,
    BriefcaseIcon,
    BuildingOffice2Icon,
    Cog6ToothIcon,
    EllipsisVerticalIcon,
    HomeIcon,
    PlusCircleIcon,
    ShoppingBagIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline'
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { classNames } from '../../../utils/classes';
import { useDispatch, useSelector } from 'react-redux';
import Gravatar from 'react-gravatar'
import { logout } from "../../../store/auth/authReducer"
import logo from '../../../assets/images/gurulabs-logo-green.png'

const initialNavigation = [
    { name: "Dashboard", href: "/user/dashboard", icon: HomeIcon, current: false },
    { name: "My Businesses", href: "/user/businesses", icon: BuildingOffice2Icon, current: false },
    { name: "My Shopping", href: "/user/shop", icon: ShoppingBagIcon, current: false },
    { name: "My Orders", href: "/user/orders", icon: BriefcaseIcon, current: false },
];

const userNavigation = [
    { name: 'Your Profile', href: '/user/profile' },
    { name: 'Billing Info', href: '/user/billing' },
]


const PrivateLayout = () => {
    const dispatch = useDispatch()
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const { user } = useSelector((state: any) => state.auth)
    const [navigation, setNavigation] = useState(initialNavigation);
    const { items } = useSelector((state) => state.cart.cart || { items: [] }); // Get items from the cart

    useEffect(() => {
        setNavigation((prevNav) =>
            prevNav.map((item) => ({
                ...item,
                current: location.pathname === item.href, // Check if URL matches
            }))
        );
    }, [location.pathname]);

    const handleLogout = () => {
        dispatch(logout())
    }

    const itemCount = items.reduce((total, item) => total + item.quantity, 0);

    const handleNavigationClick = (itemName) => {
        const updatedNavigation = navigation.map((item) =>
            item.name === itemName ? { ...item, current: true } : { ...item, current: false }
        );
        setNavigation(updatedNavigation);
        setSidebarOpen(false);
    };

    return <div>
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
            />

            <div className="fixed inset-0 flex">
                <DialogPanel
                    transition
                    className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
                >
                    <TransitionChild>
                        <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                            <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                                <span className="sr-only">Close sidebar</span>
                                <XMarkIcon aria-hidden="true" className="size-6 text-white" />
                            </button>
                        </div>
                    </TransitionChild>

                    {/* Sidebar component, swap this element with another sidebar if you like */}
                    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                        <div className="flex h-16 shrink-0 items-center">
                            <Link to="/">
                                <img
                                    alt="Gurulabs Portal"
                                    src={logo}
                                    className="h-14 w-auto"
                                />
                            </Link>
                        </div>
                        <nav className="flex flex-1 flex-col">
                            <ul role="list" className="flex flex-1 flex-col gap-y-7">
                                <li>
                                    <ul role="list" className="-mx-2 space-y-1">
                                        {navigation.map((item) => (
                                            <li key={item.name}>
                                                <Link
                                                    to={item.href}
                                                    onClick={() => handleNavigationClick(item.name)}
                                                    className={classNames(
                                                        item.current
                                                            ? 'bg-gray-50 text-green-600'
                                                            : 'text-gray-700 hover:bg-gray-50 hover:text-green-600',
                                                        'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                                                    )}
                                                >
                                                    <item.icon
                                                        aria-hidden="true"
                                                        className={classNames(
                                                            item.current ? 'text-green-600' : 'text-gray-400 group-hover:text-green-600',
                                                            'size-6 shrink-0',
                                                        )}
                                                    />
                                                    {item.name}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </li>

                                <li className="mt-auto">
                                    <Link
                                        to="/user/settings"
                                        className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 hover:text-green-600"
                                    >
                                        <Cog6ToothIcon
                                            aria-hidden="true"
                                            className="size-6 shrink-0 text-gray-400 group-hover:text-green-600"
                                        />
                                        Settings
                                    </Link>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
            {/* Sidebar component, swap this element with another sidebar if you like */}
            <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
                <div className="flex h-16 shrink-0 items-center">
                    <Link to="/">
                        <img
                            alt="Gurulabs Portal"
                            src={logo}
                            className="h-14 w-auto"
                        />
                    </Link>
                </div>
                <nav className="flex flex-1 flex-col">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                        <li>
                            <ul role="list" className="-mx-2 space-y-1">
                                {navigation.map((item) => (
                                    <li key={item.name}>
                                        <Link
                                            to={item.href}
                                            onClick={() => handleNavigationClick(item.name)}
                                            className={classNames(
                                                item.current
                                                    ? 'bg-gray-50 text-green-600'
                                                    : 'text-gray-700 hover:bg-gray-50 hover:text-green-600',
                                                'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                                            )}
                                        >
                                            <item.icon
                                                aria-hidden="true"
                                                className={classNames(
                                                    item.current ? 'text-green-600' : 'text-gray-400 group-hover:text-green-600',
                                                    'size-6 shrink-0',
                                                )}
                                            />
                                            {item.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </li>

                        <li className="mt-auto">
                            <Link
                                to="/user/settings"
                                className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 hover:text-green-600"
                            >
                                <Cog6ToothIcon
                                    aria-hidden="true"
                                    className="size-6 shrink-0 text-gray-400 group-hover:text-green-600"
                                />
                                Settings
                            </Link>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>

        <div className="lg:pl-72">
            <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
                <button type="button" onClick={() => setSidebarOpen(true)} className="-m-2.5 p-2.5 text-gray-700 lg:hidden">
                    <span className="sr-only">Open sidebar</span>
                    <Bars3Icon aria-hidden="true" className="size-6" />
                </button>

                {/* Separator */}
                <div aria-hidden="true" className="h-6 w-px bg-gray-200 lg:hidden" />

                <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
                    <form action="#" method="GET" className="grid flex-1 grid-cols-1">
                        <input
                            name="search"
                            type="search"
                            placeholder="Search"
                            aria-label="Search"
                            className="col-start-1 row-start-1 block size-full bg-white pl-8 text-base text-gray-900 outline-none placeholder:text-gray-400 sm:text-sm/6"
                        />
                        <MagnifyingGlassIcon
                            aria-hidden="true"
                            className="pointer-events-none col-start-1 row-start-1 size-5 self-center text-gray-400"
                        />
                    </form>
                    <div className="flex items-center gap-x-4 lg:gap-x-6">
                        <button type="button" className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                            <span className="sr-only">View notifications</span>
                            <BellIcon aria-hidden="true" className="size-6" />
                        </button>

                        <Link to="/user/cart" className="relative p-2 text-gray-400 hover:text-gray-500">
                            <span className="sr-only">View cart</span>
                            <ShoppingBagIcon className="h-6 w-6" />
                            {itemCount > 0 && (
                                <span className="absolute -top-0 -right-1 flex h-5 w-5 items-center text-center justify-center rounded-full bg-red-600 text-xs text-white">
                                    {itemCount}
                                </span>
                            )}
                        </Link>


                        {/* Separator */}
                        <div aria-hidden="true" className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" />

                        {/* Profile dropdown */}
                        <Menu as="div" className="relative">
                            <MenuButton className="-m-1.5 flex items-center p-1.5">
                                <span className="sr-only">Open user menu</span>
                                <Gravatar
                                    email={user?.email}
                                    className="size-8 rounded-full bg-gray-50"
                                />
                                <span className="hidden lg:flex lg:items-center">
                                    <span aria-hidden="true" className="ml-4 text-sm/6 font-semibold text-gray-900">
                                        {user?.first_name} {user?.last_name}
                                    </span>
                                    <ChevronDownIcon aria-hidden="true" className="ml-2 size-5 text-gray-400" />
                                </span>
                            </MenuButton>
                            <MenuItems
                                transition
                                className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                            >
                                {userNavigation.map((item) => (
                                    <MenuItem key={item.name}>
                                        <Link
                                            to={item.href}
                                            className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none"
                                        >
                                            {item.name}
                                        </Link>
                                    </MenuItem>
                                ))}
                            </MenuItems>
                        </Menu>

                        <Menu as="div" className="relative inline-block text-left">
                            <div>
                                <Menu.Button className="-my-2 flex items-center rounded-full bg-transparent p-2 text-gray-900 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-white">
                                    <span className="sr-only">Open options</span>
                                    <EllipsisVerticalIcon
                                        className="h-5 w-5 text-gray-900"
                                        aria-hidden="true"
                                    />
                                </Menu.Button>
                            </div>

                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <div className="py-1">
                                        <Menu.Item>
                                            {({ active }) => (
                                                <Link
                                                    onClick={handleLogout}
                                                    to="#"
                                                    className={classNames(
                                                        active
                                                            ? 'bg-gray-100 text-gray-900 hover:bg-gray-100'
                                                            : 'text-gray-700',
                                                        'flex justify-between px-4 py-2 text-sm'
                                                    )}
                                                >
                                                    <span>Sign Out</span>
                                                </Link>
                                            )}
                                        </Menu.Item>
                                    </div>
                                </Menu.Items>
                            </Transition>
                        </Menu>

                    </div>
                </div>
            </div>

            <main className="py-10">
                <div className="px-4 sm:px-6 lg:px-8">
                    <Outlet />
                </div>
            </main>
        </div>
    </div>
}
export default PrivateLayout